import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from '../context/AuthContext';
import './QualityAssurance.css';
import { ArrowLeft } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export default function QualityAssurance() {
  const { user, token, logout } = useAuth();
  const [pendingQA, setPendingQA] = useState([]);
  const [qualityChecks, setQualityChecks] = useState([]);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [qaForm, setQaForm] = useState({
    stockMovementId: "",
    condition: "",
    quantityChecked: "",
    quantityPassed: "",
    failureReason: "",
    notes: ""
  });

  // Tab state
  const [currentTab, setCurrentTab] = useState("pending"); // 'pending' or 'records'

  const openModal = (stockMovementId) => {
    setQaForm({
      stockMovementId,
      condition: "",
      quantityChecked: "",
      quantityPassed: "",
      failureReason: "",
      notes: ""
    });
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  const fetchJson = async (url) => {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  };

  const loadPendingQA = async () => {
    try {
      setLoading(true);
      const data = await fetchJson(`${API_BASE}/Inventory/pending-qa-stock`);
      sessionStorage.setItem("pendingQAs", JSON.stringify(data));
       
      setPendingQA(data);
    } catch (err) {
      setError("Failed to load pending QA stock.");
    } finally {
      setLoading(false);
    }
  };

  const loadQualityChecks = async () => {
    try {
      setLoading(true);
      let url = `${API_BASE}/Inventory/quality-checks?page=${page}&pageSize=${pageSize}`;
      const data = await fetchJson(url);
      setQualityChecks(data);
      sessionStorage.removeItem("inventory");
    } catch (err) {
      setError("Failed to load quality check records.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPendingQA();
    loadQualityChecks();
  }, [page]);

  const handleSearch = () => {
    setPage(1);
    loadQualityChecks();
  };

  const submitQA = async () => {
    try {
      setError("");
      setLoading(true);
      const res = await fetch(`${API_BASE}/Inventory/process-stock-qa`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          stockMovementId: qaForm.stockMovementId,
          condition: qaForm.condition,
          quantityChecked: Number(qaForm.quantityChecked),
          quantityPassed: Number(qaForm.quantityPassed),
          failureReason: qaForm.failureReason || null,
          checkedBy: user.fullName,
          notes: qaForm.notes || null,
        }),
      });

      if (!res.ok) throw new Error(await res.text() || "Failed processing QA");

      closeModal();
      await loadPendingQA();
      await loadQualityChecks();
    } catch (err) {
      setError("Failed to process QA: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Back to Dashboard Function
  const handleBackToDashboard = () => {
    window.location.href = '/dashboard';
   
  };

  const [filters, setFilters] = useState({
    search: "",
    productName: "",
    warehouseName: "",
    startDate: "",
    endDate: ""
  });

  const uniqueProductNames = useMemo(() => {
    const namesSet = new Set();
    pendingQA.forEach(p => namesSet.add(p.productName));
    return Array.from(namesSet).sort();
  }, [pendingQA]);

  const uniqueWarehouseNames = useMemo(() => {
    const namesSet = new Set();
    pendingQA.forEach(p => namesSet.add(p.warehouseName));
    return Array.from(namesSet).sort();
  }, [pendingQA]);
  
  const filteredPendingQAs = pendingQA.filter(p =>
    (!filters.productName || p.id === filters.productName || p.productName === filters.productName) &&

    (!filters.warehouseName || p.id === filters.warehouseName || p.warehouseName === filters.warehouseName) && 

    (!filters.startDate || new Date(p.timestamp) >= new Date(filters.startDate)) &&

    (!filters.endDate || new Date(p.timestamp) <= new Date(filters.endDate))
  );

  const filteredQualityChecks = qualityChecks.filter(q =>
    (!filters.productName || q.id === filters.productName || q.productName === filters.productName) &&

    (!filters.warehouseName || q.id === filters.warehouseName || q.warehouseName === filters.warehouseName) && 

    (!filters.startDate || new Date(q.checkedDate) >= new Date(filters.startDate)) &&

    (!filters.endDate || new Date(q.checkedDate) <= new Date(filters.endDate))
  );

  const hasNextPage = filteredQualityChecks.length === pageSize;

  return (
    <div className="qa-container">
      {/* Header with user info and logout */}
      <div className="info-header">
        <div>
          <h3 style={{ color: '#2d5016', margin: 0 }}>
            {user?.fullName || `${user?.firstName} ${user?.lastName}` || user?.email}
          </h3>
          <h3 style={{ color: '#2d5016', margin: 0 }}>{user?.companyName}</h3>
          <p style={{ color: '#666', fontSize: '14px', margin: '4px 0 0 0' }}>
            {user?.email} - {user?.role}
          </p>
        </div>
        <button 
          onClick={logout}
          style={{
            background: '#ff8c00',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Logout
        </button>
      </div>
      <div className= "qa-content">
        <div className="qa-header">
          <h1 className="qa-title">QA Inventory Overview</h1>
          <div className="header-actions">
            <button 
              className="back-btn" 
              onClick={handleBackToDashboard}
            >
              <ArrowLeft style={{ width: '16px', height: '16px' }} />
                Back to Dashboard
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="qa-tabs">
          <button
            className={currentTab === "pending" ? "active-tab" : ""}
            onClick={() => setCurrentTab("pending")}
          >
            Pending QA Stock
          </button>
          <button
            className={currentTab === "records" ? "active-tab" : ""}
            onClick={() => setCurrentTab("records")}
          >
            Quality Check Records
          </button>
        </div>

        <div className="filters-section">
          <div className="form-group filter-group">
            <select
              value={filters.productName}
              onChange={(e) => setFilters({ ...filters, productName: e.target.value })}
            >
              <option value="">All</option>
              {uniqueProductNames.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <label>Product</label>
          </div>

          <div className="form-group filter-group">
            <select
              value={filters.warehouseName}
              onChange={(e) => setFilters({ ...filters, warehouseName: e.target.value })}
            >
              <option value="">All</option>
              {uniqueWarehouseNames.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <label>Warehouse</label>
          </div>

          <div className="form-group filter-group">
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            />
            <label>Start Date</label>
          </div>

          <div className="form-group filter-group">
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            />
            <label>End Date</label>
          </div>
        </div>

        {loading && <p className="loading">Loading...</p>}
        {error && <p className="error-message">{error}</p>}

        {/* Pending QA Stock Tab */}
        {currentTab === "pending" && (
          
          <section className="qa-table-section">
            <table className="qa-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Warehouse</th>
                  <th>Date</th>
                  <th>Quantity</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredPendingQAs.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center p-4">
                      No pending QA stock found.
                    </td>
                  </tr>
                ) : (
                  filteredPendingQAs.map((item) => (
                    <tr key={item.id}>
                      <td>{item.productName}</td>
                      <td>{item.warehouseName}</td>
                      <td>{new Date(item.timestamp).toLocaleString()}</td>
                      <td>{item.quantity}</td>
                      <td>
                        <button
                          onClick={() => openModal(item.id)}
                          className="process-btn"
                        >
                          Process QA
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>
        )}

        {/* Quality Check Records Tab */}
        {currentTab === "records" && (
          <section className="qa-table-section">
            <table className="qa-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Warehouse</th>
                  <th>Quantity Received</th>
                  <th>Quantity Checked</th>
                  <th>Quantity Passed</th>
                  <th>Quantity Failed</th>
                  <th>Checked By</th>
                  <th>Date Checked</th>
                  <th>Condition</th>
                  <th>Failure reason</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {filteredQualityChecks.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="text-center p-4">
                      No quality checks found.
                    </td>
                  </tr>
                ) : (
                  filteredQualityChecks.map((qc) => (
                    <tr key={qc.id}>
                      <td>{qc.productName}</td>
                      <td>{qc.warehouseName}</td>
                      <td>{qc.quantityReceived}</td>
                      <td>{qc.quantityChecked}</td>
                      <td>{qc.quantityPassed}</td>
                      <td>{qc.quantityFailed}</td>
                      <td>{qc.checkedBy}</td>
                      <td>{new Date(qc.checkedDate).toLocaleString()}</td>
                      <td>{qc.condition}</td>
                      <td>{qc.failureReason}</td>
                      <td>{qc.notes}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="qa-pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="process-btn"
              >
                Previous
              </button>
              <span>Page {page}</span>
              <button
                onClick={() => {
                  if (hasNextPage) setPage(page + 1);
                }}
                disabled={!hasNextPage}
                className="process-btn"
              >
                Next
              </button>
            </div>
          </section>
        )}

        {/* QA Modal */}
        {showModal && (
          <div className="qa-modal-overlay">
            <div className="qa-modal">
              <h2>Process Stock QA</h2>
              <div className="modal-form">
                
                <div className="form-group">
                  <input
                    type="number"
                    placeholder=" "
                    value={qaForm.quantityChecked}
                    onChange={(e) => setQaForm({ ...qaForm, quantityChecked: e.target.value })}
                  />
                  <label>Quantity Checked</label>
                </div>
                <div className="form-group">
                  <input
                    type="number"
                    placeholder=" "
                    value={qaForm.quantityPassed}
                    onChange={(e) => setQaForm({ ...qaForm, quantityPassed: e.target.value })}
                  />
                  <label>Quantity Passed</label>
                </div>
                <label>Condition:</label>
                <div className="compact-radio-group">
                  
                  <label className="compact-radio">
                    <input
                      type="radio"
                      name="overallCondition"
                      value="Good"
                      checked={qaForm.condition === "Good"}
                      onChange={(e) =>
                        setQaForm({ ...qaForm, condition: e.target.value })
                      }
                    />
                    Good
                  </label>

                  <label className="compact-radio">
                    <input
                      type="radio"
                      name="overallCondition"
                      value="Damaged"
                      checked={qaForm.condition === "Damaged"}
                      onChange={(e) =>
                        setQaForm({ ...qaForm, condition: e.target.value })
                      }
                    />
                    Damaged
                  </label>
                </div>
                <div className="form-group">
                  <input
                    placeholder=" "
                    value={qaForm.failureReason}
                    onChange={(e) => setQaForm({ ...qaForm, failureReason: e.target.value })}
                  />
                  <label>Failure Reason (optional)</label>
                </div>
                <div className="form-group">
                  <textarea
                    placeholder=" "
                    value={qaForm.notes}
                    onChange={(e) => setQaForm({ ...qaForm, notes: e.target.value })}
                  />
                  <label>Notes (optional)</label>
                </div>
              </div>
              <div className="modal-actions">
                <button className="cancel-btn" onClick={closeModal}>Cancel</button>
                <button className="submit-btn" onClick={submitQA}>Submit QA</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
